import React, { useState } from 'react';
import { X, Loader, User, Building2 } from 'lucide-react';
import { theme } from '../theme';
import { apiRequest } from '../utils/apiUtils';
import { useAuth } from '../contexts/AuthContext';

interface DevLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DevLoginModal: React.FC<DevLoginModalProps> = ({ isOpen, onClose }) => {
  const { setToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: 'test@futbolero.com',
    role: 'PLAYER' as 'PLAYER' | 'VENUE_OWNER',
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Usar apiRequest con requireAuth: false porque estamos haciendo login
      const response = await apiRequest('/api/auth/dev-login', {
        method: 'POST',
        body: {
          email: formData.email,
          role: formData.role,
        },
        requireAuth: false, // IMPORTANTE: No requerir auth para el login
      });

      // Verificar si hubo error
      if (response.error) {
        setError(response.error);
        setLoading(false);
        return;
      }

      // Extraer token del response
      const { token } = response.data || {};

      if (!token) {
        setError('No se recibió token del servidor');
        setLoading(false);
        return;
      }

      // Guardar token en localStorage (misma key que AuthContext)
      localStorage.setItem('authToken', token);

      // Actualizar el context
      if (setToken) {
        setToken(token);
      }

      // Recargar la página para actualizar el estado
      window.location.reload();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error en dev login:', err);
      setError('Error al iniciar sesión');
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
    animation: 'fadeIn 0.2s ease-out',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.card,
    borderRadius: '24px',
    maxWidth: '500px',
    width: '100%',
    position: 'relative',
    border: `2px solid ${theme.colors.warning}`,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    animation: 'slideUp 0.3s ease-out',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.warning,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background-color 0.2s',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const warningBannerStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    border: `1px solid ${theme.colors.warning}`,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  };

  const warningTextStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    lineHeight: '1.5',
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    color: theme.colors.text,
    fontWeight: theme.fonts.weightBold,
    fontSize: theme.fonts.sizeSmall,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const roleGroupStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginBottom: '24px',
  };

  const roleCardStyle = (selected: boolean): React.CSSProperties => ({
    padding: '20px',
    backgroundColor: selected ? 'rgba(0, 230, 118, 0.1)' : 'rgba(255, 255, 255, 0.02)',
    border: `2px solid ${selected ? theme.colors.primary : theme.colors.border}`,
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  });

  const roleIconStyle: React.CSSProperties = {
    color: theme.colors.primary,
  };

  const roleTitleStyle: React.CSSProperties = {
    fontSize: '1rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const roleDescStyle: React.CSSProperties = {
    fontSize: '0.85rem',
    color: theme.colors.textSecondary,
    lineHeight: '1.4',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px',
    borderRadius: '12px',
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    backgroundColor: theme.colors.warning,
    color: '#181A1B',
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    border: '1px solid rgba(255, 107, 107, 0.3)',
    borderRadius: '12px',
    padding: '12px 16px',
    color: '#ff6b6b',
    marginBottom: '16px',
    fontSize: theme.fonts.sizeSmall,
  };

  return (
    <div style={overlayStyle} onClick={handleOverlayClick}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            🧪 Modo Desarrollo
          </h2>
          <button
            style={closeButtonStyle}
            onClick={onClose}
            disabled={loading}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={24} color={theme.colors.textSecondary} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Warning Banner */}
          <div style={warningBannerStyle}>
            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
            <div>
              <strong style={{ color: theme.colors.warning, display: 'block', marginBottom: '4px' }}>
                Solo para desarrollo
              </strong>
              <p style={warningTextStyle}>
                Este login bypasea la autenticación de Google. Solo disponible en modo desarrollo.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && <div style={errorStyle}>{error}</div>}

          {/* Form */}
          <form onSubmit={handleLogin}>
            {/* Email Input */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                style={inputStyle}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="test@futbolero.com"
                required
                disabled={loading}
              />
            </div>

            {/* Role Selection */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>Seleccioná tu rol</label>
              <div style={roleGroupStyle}>
                {/* Player Role */}
                <div
                  style={roleCardStyle(formData.role === 'PLAYER')}
                  onClick={() => !loading && setFormData({ ...formData, role: 'PLAYER' })}
                >
                  <User size={32} style={roleIconStyle} />
                  <div style={roleTitleStyle}>Jugador</div>
                  <div style={roleDescStyle}>Reservar canchas y crear partidos</div>
                </div>

                {/* Owner Role */}
                <div
                  style={roleCardStyle(formData.role === 'VENUE_OWNER')}
                  onClick={() => !loading && setFormData({ ...formData, role: 'VENUE_OWNER' })}
                >
                  <Building2 size={32} style={roleIconStyle} />
                  <div style={roleTitleStyle}>Propietario</div>
                  <div style={roleDescStyle}>Gestionar complejos y canchas</div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={buttonStyle}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 25px rgba(255, 152, 0, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {loading ? (
                <>
                  <Loader size={18} className="spinner" />
                  Iniciando sesión...
                </>
              ) : (
                <>
                  🚀 Iniciar Sesión
                </>
              )}
            </button>
          </form>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default DevLoginModal;
